import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import Swal from "sweetalert2";
import { useNavigate, useParams } from "react-router-dom";

import PageTitle from "../../layouts/PageTitle";
import { appApi, useGetCpDatabaseQuery } from "../../../store/appApi";
import { createCpApi, updateCpApi } from "../../../services/cpDatabase";

const darkPanel =
  "rounded-3xl border border-gray-200 bg-white text-gray-900 shadow-xl shadow-slate-200/70";

const inputClass =
  "w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 outline-none transition focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20";

const labelClass =
  "mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-gray-500";

const EMPTY_FORM = {
  cpName: "",
  phone: "",
  companyName: "",
  address: "",
  remarks: "",
};

const AddCp = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // With an :id param this page edits that CP entry instead of adding one.
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { data, isLoading } = useGetCpDatabaseQuery(undefined, {
    skip: !isEdit,
  });
  const existing = isEdit
    ? (data?.cps || []).find((c) => String(c._id) === String(id))
    : null;

  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!existing) return;
    setForm({
      cpName: existing.cpName || "",
      phone: existing.phone || "",
      companyName: existing.companyName || "",
      address: existing.address || "",
      remarks: existing.remarks || "",
    });
  }, [existing]);

  const updateField = (key, value) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.cpName.trim() || !form.phone.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Missing details",
        text: "CP Name and Phone are required.",
      });
      return;
    }

    try {
      setSaving(true);
      const payload = {
        cpName: form.cpName.trim(),
        phone: form.phone.trim(),
        companyName: form.companyName.trim(),
        address: form.address.trim(),
        remarks: form.remarks.trim(),
      };
      if (isEdit) await updateCpApi(id, payload);
      else await createCpApi(payload);

      dispatch(appApi.util.invalidateTags(["CpDatabase"]));

      Swal.fire({
        icon: "success",
        title: "Saved",
        text: isEdit
          ? "CP entry updated successfully."
          : "CP entry added successfully.",
        timer: 1800,
        showConfirmButton: false,
      });

      if (isEdit) navigate("/cp-database");
      else setForm(EMPTY_FORM);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Unable to save",
        text:
          error?.response?.data?.message ||
          "Something went wrong while saving this entry.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PageTitle
        activeMenu={isEdit ? "Edit CP" : "Add CP"}
        motherMenu="Workforce"
      />

      <div className="space-y-6">
        <section className={`${darkPanel} relative overflow-hidden p-6`}>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(169,126,8,0.10),_transparent_35%),radial-gradient(circle_at_bottom_left,_rgba(226,232,240,0.6),_transparent_40%)]" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-amber-700">
              CP Database
            </div>
            <h1 className="mt-4 text-3xl font-semibold text-gray-900 md:text-4xl">
              {isEdit ? "Edit Channel Partner" : "Add Channel Partner"}
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-gray-500">
              {isEdit
                ? "Update this channel partner's contact details."
                : "Record a new channel partner's contact details."}
            </p>
            <button
              type="button"
              className="mt-4 rounded-full border-2 border-amber-500 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-gray-700 transition hover:text-amber-700"
              onClick={() => navigate("/cp-database")}
            >
              View CP Database
            </button>
          </div>
        </section>

        {isEdit && !existing ? (
          <div
            className={`${darkPanel} p-10 text-center text-sm text-gray-400`}
          >
            {isLoading ? "Loading…" : "CP entry not found."}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className={`${darkPanel} p-5 md:p-6`}>
            <div className="grid gap-5 md:grid-cols-2">
              <div>
                <label className={labelClass}>CP Name</label>
                <input
                  className={inputClass}
                  autoComplete="off"
                  value={form.cpName}
                  onChange={(event) =>
                    updateField("cpName", event.target.value)
                  }
                  placeholder="Channel partner name"
                />
              </div>

              <div>
                <label className={labelClass}>Phone</label>
                <input
                  className={inputClass}
                  autoComplete="off"
                  value={form.phone}
                  onChange={(event) => updateField("phone", event.target.value)}
                  placeholder="10-digit phone number"
                />
              </div>

              <div>
                <label className={labelClass}>Company Name</label>
                <input
                  className={inputClass}
                  autoComplete="off"
                  value={form.companyName}
                  onChange={(event) =>
                    updateField("companyName", event.target.value)
                  }
                  placeholder="Company / firm name"
                />
              </div>

              <div>
                <label className={labelClass}>Address</label>
                <input
                  className={inputClass}
                  autoComplete="off"
                  value={form.address}
                  onChange={(event) =>
                    updateField("address", event.target.value)
                  }
                  placeholder="Address"
                />
              </div>

              <div className="md:col-span-2">
                <label className={labelClass}>Remarks</label>
                <textarea
                  className={`${inputClass} resize-none`}
                  rows={3}
                  value={form.remarks}
                  onChange={(event) =>
                    updateField("remarks", event.target.value)
                  }
                  placeholder="Any notes or remarks about this CP"
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end border-t border-gray-200 pt-5">
              <button
                type="submit"
                disabled={saving}
                className="rounded-full bg-amber-500 px-5 py-3 text-xs font-semibold uppercase tracking-[0.22em] text-slate-950 transition hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? "Saving..." : isEdit ? "Save Changes" : "Add CP"}
              </button>
            </div>
          </form>
        )}
      </div>
    </>
  );
};

export default AddCp;
